import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { showToast } from './Toast';
import { StatusBadge } from './Badges';

const DAMAGE_PANELS = [
  'Front Bumper','Hood','Roof','Rear Bumper',
  'Driver Side','Passenger Side','Windscreen','Wheels',
];

const CHECKLISTS = {
  workshop: {
    title: '🔧 Workshop Checklist',
    sections: [
      { title: 'Vehicle Condition on Arrival', items: [
        { label: 'Inspect for existing damage', sub: 'Note any dents, scratches, chips on all panels, glass, wheels', dmg: true },
        { label: 'Interior condition check', sub: 'Note any stains, damage or missing items' },
      ]},
      { title: 'Pre-Delivery Service', items: [
        { label: 'Engine oil checked / filled', sub: 'Check level and top up if needed' },
        { label: 'Coolant and brake fluid checked', sub: 'All fluid levels verified' },
        { label: 'Tyre pressure checked and adjusted', sub: 'All 4 tyres + spare' },
        { label: 'Battery health checked', sub: 'EV: charge level · Petrol: 12V battery ok' },
        { label: 'All lights functional', sub: 'Headlights, indicators, reversing, brake lights' },
        { label: 'Windscreen washer fluid topped up', sub: '' },
      ]},
      { title: 'Handover', items: [
        { label: 'Keys labelled with stock number', sub: '' },
        { label: 'Vehicle ready for Tint / Detail', sub: 'Car is clean and accessible' },
      ]},
    ],
  },
  tint: {
    title: '🪟 Tint Checklist',
    sections: [
      { title: 'Vehicle Condition on Arrival', items: [
        { label: 'Inspect for existing damage', sub: 'Note any damage before starting work', dmg: true },
        { label: 'Interior clean enough to work', sub: 'Windows and panels are accessible' },
      ]},
      { title: 'Tint Installation', items: [
        { label: 'Correct tint % used as per work order', sub: 'Confirm specification with job sheet' },
        { label: 'No bubbles in tint film', sub: 'Inspect entire surface under good lighting' },
        { label: 'Clean edges — no lifting or peeling', sub: 'Check all window edges and corners' },
        { label: 'Rear window heating element not damaged', sub: 'Defroster lines intact' },
        { label: 'All specified windows completed', sub: 'Confirm all required windows are done' },
      ]},
      { title: 'Final Check', items: [
        { label: 'Interior clean — no residue or off-cuts', sub: 'Remove all waste material from car' },
        { label: 'Vehicle ready for Detail or Delivery', sub: '' },
      ]},
    ],
  },
  detail: {
    title: '✨ Detail Checklist',
    sections: [
      { title: 'Vehicle Condition on Arrival', items: [
        { label: 'Inspect for existing damage', sub: 'Note any dents, scratches before starting work', dmg: true },
      ]},
      { title: 'Interior Detail', items: [
        { label: 'Vacuum entire interior', sub: 'Seats, floor, boot, under seats' },
        { label: 'Wipe all surfaces and panels', sub: 'Dashboard, door cards, centre console' },
        { label: 'Clean and dress all plastic trim', sub: '' },
        { label: 'Windows cleaned — interior side', sub: 'No streaks or smears' },
        { label: 'Seats cleaned / leather dressed', sub: 'If applicable' },
      ]},
      { title: 'Exterior Detail', items: [
        { label: 'Full exterior wash and dry', sub: '' },
        { label: 'Wheels and tyres cleaned and dressed', sub: '' },
        { label: 'Exterior windows cleaned', sub: 'No streaks or smears' },
      ]},
      { title: 'Final Check', items: [
        { label: 'Car presentable for client delivery', sub: 'Final walk-around completed' },
        { label: 'No damage caused during detail', sub: 'Confirm no new marks or scratches' },
      ]},
    ],
  },
  noah: {
    title: '📋 Final Delivery Checklist',
    sections: [
      { title: 'Services Verification (from sales order)', items: [], auto: true },
      { title: 'Vehicle Inspection', items: [
        { label: 'Full damage inspection', sub: 'Walk-around — note anything before client arrives', dmg: true },
        { label: 'Vehicle is clean inside and out', sub: 'Presentable for client handover' },
        { label: 'All ordered accessories in the car', sub: 'Check against original sales order' },
      ]},
      { title: 'Technical Check', items: [
        { label: 'Battery / Fuel Level', sub: 'EV: minimum 80% charge · Petrol: adequate fuel' },
        { label: 'Number plates installed', sub: 'OR note if customer is using their own plates' },
        { label: 'EV compliance tag on plates', sub: 'Required for all electric vehicles — mandatory', evOnly: true },
        { label: 'All lights and electrics working', sub: 'Quick check of main functions' },
      ]},
      { title: 'Documentation & Handover', items: [
        { label: 'Vehicle manuals in car', sub: "Owner's manual, service book, warranty info" },
        { label: 'Both keys accounted for', sub: 'Main key + spare key' },
        { label: 'Registration papers in order', sub: '' },
      ]},
    ],
  },
};

function buildItems(type, vehicle) {
  const cfg = CHECKLISTS[type];
  if (!cfg) return [];
  const items = [];
  cfg.sections.forEach((sec) => {
    if (sec.auto) return; // services-verification: not interactive checklist items
    sec.items.forEach((it) => {
      if (it.evOnly && !vehicle.is_ev) return;
      items.push({ section: sec.title, label: it.label, sub: it.sub, dmg: !!it.dmg });
    });
  });
  return items;
}

export default function ChecklistModal({ open, type, vehicle, onClose, onCompleted }) {
  const cfg = type ? CHECKLISTS[type] : null;
  const items = useMemo(() => (vehicle ? buildItems(type, vehicle) : []), [type, vehicle]);
  const [state, setState] = useState({}); // { idx: { status, note, panels:[] } }
  const [saving, setSaving] = useState(false);

  // Reset whenever modal opens for a different vehicle/type
  React.useEffect(() => {
    if (open) setState({});
  }, [open, type, vehicle?.id]);

  if (!open || !vehicle || !cfg) return null;

  function setItem(idx, patch) {
    setState((s) => ({ ...s, [idx]: { ...(s[idx] || { panels: [] }), ...patch } }));
  }

  function togglePanel(idx, panel) {
    setState((s) => {
      const cur = s[idx] || { panels: [] };
      const panels = cur.panels.includes(panel)
        ? cur.panels.filter((p) => p !== panel)
        : [...cur.panels, panel];
      return { ...s, [idx]: { ...cur, panels } };
    });
  }

  const checked = Object.values(state).filter((x) => x.status).length;

  async function complete() {
    if (saving) return;
    setSaving(true);
    try {
      const payloadItems = items.map((it, i) => ({
        section: it.section,
        label: it.label,
        status: state[i]?.status || null,
        note: state[i]?.note || null,
        damage_panels: it.dmg ? (state[i]?.panels || []) : undefined,
      }));
      const damageFound = payloadItems.some(
        (it) => (it.damage_panels && it.damage_panels.length) || it.status === 'fail'
      );
      const damageNotes = payloadItems
        .filter((it) => it.note)
        .map((it) => `[${it.label}] ${it.note}`)
        .join('\n');

      await api.submitChecklist(vehicle.id, {
        type,
        items: payloadItems,
        damage_found: damageFound,
        damage_notes: damageNotes || null,
        completion_status: 'done',
      });
      showToast('Checklist complete — Status updated!');
      onCompleted?.();
      onClose();
    } catch (e) {
      showToast('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mo open" onClick={(e) => { if (e.target.classList.contains('mo')) onClose(); }}>
      <div className="modal">
        <div className="mh">
          <div>
            <div className="mtit">{cfg.title}</div>
            <div style={{ marginTop: 3, fontSize: 12, color: 'var(--muted)' }}>
              {vehicle.model} — {vehicle.stock_number}
            </div>
          </div>
          <button className="mbclose" onClick={onClose}>✕</button>
        </div>

        <div className="mbody">
          {cfg.sections.map((sec, sIdx) => {
            if (sec.auto) {
              const services = vehicle.services || [];
              return (
                <div className="cl-sec" key={sIdx}>
                  <div className="cl-sec-title">{sec.title}</div>
                  <div className="auto-grid">
                    {services.length === 0 && (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>No services on this vehicle.</div>
                    )}
                    {services.map((s, i) => {
                      const status = s.includes('Tint') ? vehicle.status_tint
                        : s.includes('Paint') ? vehicle.status_paint
                        : s.includes('Detail') ? vehicle.status_detail
                        : vehicle.status_workshop;
                      return (
                        <div className="ai" key={i}>
                          <span className="ai-lbl">{s}</span>
                          <StatusBadge value={status} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            // Find indices for this section
            const sectionItems = sec.items
              .filter((it) => !it.evOnly || vehicle.is_ev)
              .map((it) => {
                const idx = items.findIndex((x) => x.section === sec.title && x.label === it.label);
                return { ...it, idx };
              });

            return (
              <div className="cl-sec" key={sIdx}>
                <div className="cl-sec-title">{sec.title}</div>
                {sectionItems.map(({ idx, label, sub, dmg }) => {
                  const cur = state[idx] || { panels: [] };
                  const isOk   = cur.status === 'ok';
                  const isFail = cur.status === 'fail';
                  return (
                    <div className="cl-item" key={idx}>
                      <div className={`cl-chk ${isOk ? 'ok' : isFail ? 'fail' : ''}`}>
                        {isOk ? '✓' : isFail ? '✗' : ''}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="cl-lbl">{label}</div>
                        {sub && <div className="cl-sub">{sub}</div>}
                        <div className="cl-acts">
                          <button
                            className="cl-ok"
                            type="button"
                            onClick={() => setItem(idx, { status: 'ok' })}
                          >✓ OK</button>
                          <button
                            className="cl-fail"
                            type="button"
                            onClick={() => setItem(idx, { status: 'fail' })}
                          >✗ Issue</button>
                        </div>
                        {dmg && isFail && (
                          <div className="dmg-wrap">
                            {DAMAGE_PANELS.map((p) => (
                              <span
                                key={p}
                                className={`dmg-tag ${(cur.panels || []).includes(p) ? 'sel' : ''}`}
                                onClick={() => togglePanel(idx, p)}
                              >{p}</span>
                            ))}
                          </div>
                        )}
                        {isFail && (
                          <div className="cl-note">
                            <input
                              type="text"
                              placeholder="Describe the issue..."
                              value={cur.note || ''}
                              onChange={(e) => setItem(idx, { note: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="mfoot" style={{ justifyContent: 'space-between' }}>
          <div className="cl-prog">{checked} / {items.length} checked</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cn" onClick={onClose}>Back</button>
            <button className="cl-complete" disabled={saving} onClick={complete}>
              {saving ? 'Saving…' : '✓ Mark Complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
